import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import Dropzone from "../../../../../../components/dropzone/dropzone";
import ProgressBarField from "../../../../../../components/progressBarField/progressBarField";
import { UploadProcessStatus } from "../../../../../../components/progressBarField/progressBarInfo/types/progressBarInfoProps";
import Table from "../../../../../../components/shared/table/table";
import { notifyError } from "../../../../../../components/snackbar/snackbar";
import { RastersAttributes } from "../../../../../../contexts/poFormContext";
import { UploadProps } from "../types/uploadProps";
import { apiContext } from "../../../../../../contexts/apiContext";
import KrFormContext, { EndangeredAreaRasters, KrEndangeredAreaFormTypes, krFormTypes, KrLandslideFormTypes, LandslideRasters } from "../../../../../../contexts/krFormContext";
import { UploadRegistrationCardFileProps } from "../../../../../../services/uploadFilesService/types/uploadFilesServiceProps";
import { uploadRegistrationCardFile } from "../../../../../../services/uploadFilesService/uploadFilesService";
import { AddRegistrationCardRastersProps } from "../../../../../../services/registrationCardService/types/registrationCardServiceProps";
import { addRegistrationCardRasters } from "../../../../../../services/registrationCardService/registrationCardService";
import imageCompression from "browser-image-compression";
import { ProgressBarFieldProps } from "../../../../../../components/progressBarField/types/progressBarFieldProps";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import TableActions from "../../../../../../components/shared/table/tableActions/tableActions";
import { FormFieldProps } from "../../../../../../components/formField/types/formFieldProps";
import { classNameBtn } from "../../../../../../components/shared/button/types/buttonProps";
import { stylizationVerticalAlign } from "../../../../../../components/shared/stylization";
import { ButtonFieldProps } from "../../../../../../components/buttonField/types/buttonFieldProps";
import FormField from "../../../../../../components/formField/formField";
import ButtonField from "../../../../../../components/buttonField/buttonField";
import { HeadersTable } from "../../../../../../helpers/featureTableConstruct";
import { checkIfMarkedForCommentFilter, fireAddCommentBtnOnClickForRow, getFieldRefForRow, getKRDictFieldNameForRow } from "../../../../../../helpers/form";
import { _changeDescription, appendAttributes, appendRasters, getMaxKolejnosc } from "./rasterUtils";
import { getErrorMessage } from "../../../../../../helpers/errorHelper";

const UploadRasters = (props: UploadProps) => {
    const api = useContext(apiContext);
    const { krForm, setkrForm } = useContext(KrFormContext);
    const [uploadedImages, setUploadedImages] = useState<RastersAttributes[]>([]);
    const [progress, setProgress] = useState<boolean>(false);
    const [visibilityRows, setVisibilityRows] = useState<string[]>([]);
    const [rowsToFoldOut, setRowsToFoldOut] = useState<string[]>([]);
    const [reload, setReload] = useState(false);
    const [tableData, setTableData] = useState<RastersAttributes[]>([]);

    useEffect(() => {
        let data: RastersAttributes[];
        if (props.isLandslide) {
            data = appendAttributes(structuredClone((krForm as KrLandslideFormTypes)).landslideRasters, props.rasterType, props.isMaps);
        } else {
            data = appendAttributes(structuredClone((krForm as KrEndangeredAreaFormTypes)).endangeredAreaRasters, props.rasterType, props.isMaps);
        }
        setTableData(data);
    }, [krForm, props.isLandslide, props.isMaps, props.rasterType]);

    // File dropzone
    const handleConfirm = async (files: File[]) => {
        if (files.length > props.maxFileNumberForUpload) {
            notifyError({ message: `Próba dodania plików zakończona niepowodzeniem - maksymalna liczba wgrywanych plików to ${props.maxFileNumberForUpload}` });
            return;
        }
        const objectIdNum = props.isLandslide ? (krForm as KrLandslideFormTypes).landslideAttributes?.numeR_IDENTYFIKACYJNY! : (krForm as KrEndangeredAreaFormTypes).endangeredAreaAttributes?.numeR_IDENTYFIKACYJNY!;

        setUploadedImages([]);
        let rasters: RastersAttributes[] = [];
        Promise.all(files.map(async (file, index: number) => {
            // Resize file if size or dimensions over limit
            return new Promise((resolve) => {
                const img = new Image();
                img.src = URL.createObjectURL(file);
                img.onload = () => {
                    URL.revokeObjectURL(img.src);
                    resolve({ height: img.height, width: img.width });
                }
            }).then(async (res: any) => {
                if (file.size > props.maxFileSize || res.height > props.maxFileDimensions || res.width > props.maxFileDimensions) {
                    await resizeFile(file).then((response: any) => {
                        file = response as File;
                    }).catch((err: any) => {
                        throw Error(`Błąd kompresji - ${err?.message}`);
                    })
                }

                // Add file to progress list (uploadedFiles) with uniqe local id, set starting progress values
                const id = crypto.randomUUID();
                const uploadedFile = {
                    rodzaj: props.rasterType,
                    localUUID: id,
                    pliK_NAZWA: file.name,
                    uploadPercent: 0,
                    currentUploadStatus: UploadProcessStatus.Uploading
                };
                rasters.push(uploadedFile);
                setUploadedImages([...rasters]);

                // Show progress bars
                if (!progress) setProgress(true);

                // Update progress values 
                const handleOnUploadProgress = (progressEvent: any) => {
                    const perc = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    rasters = rasters.map(r => r.localUUID === id ? { ...r, uploadPercent: perc, currentUploadStatus: perc === 100 ? UploadProcessStatus.Saving : r.currentUploadStatus } : r);
                    setUploadedImages([...rasters]);
                }

                const uploadRegistrationCardFileProps: UploadRegistrationCardFileProps = {
                    file: file,
                    isLandslide: props.isLandslide,
                    idNum: objectIdNum,
                    waitingRoomGlobalId: krForm.waitingRoomGlobalId ? krForm.waitingRoomGlobalId : "",
                    idx: index,
                    apiBase: api.apiUrl + api.apiVersion,
                    handleOnUploadProgress: handleOnUploadProgress,
                    rasterType: props.rasterType!,
                    monitoringType: "",
                    folder: "Rastry"
                }
                // Upload files
                return uploadRegistrationCardFile(uploadRegistrationCardFileProps)
                    .then((res: any) => {
                        rasters = rasters.map(r => r.localUUID === id ? { ...r, fileNameModified: res[1] } : r);
                        setUploadedImages([...rasters]);
                        return {
                            path: res[0],
                            raster: {
                                rodzaj: props.rasterType,
                                pliK_NAZWA: file.name,
                                fileNameModified: res[1]
                            } as RastersAttributes
                        };
                    })
            })
        })).then((responseUpload) => {
            //Common paths for all uploaded files
            const filePath = responseUpload[0].path;
            const baseIdx = getMaxKolejnosc(krForm, props.isLandslide);
            const addRasters = responseUpload.map((r, idx) => {
                return { ...r.raster, kolejnosc: baseIdx + idx };
            })
            // Add rasters to db (files from progress list - rasters)
            const addRegistrationCardRastersProps: AddRegistrationCardRastersProps = {
                rastersAttributes: addRasters,
                objectIdNumber: objectIdNum,
                isLandslide: props.isLandslide,
                waitingRoomGlobalId: krForm.waitingRoomGlobalId,
                filePath: filePath,
                apiBase: api.apiUrl + api.apiVersion
            }

            return addRegistrationCardRasters(addRegistrationCardRastersProps)
                .then((response: any) => {
                    let newForm: krFormTypes | null = null;
                    // Add rasters with completed attributes to the object
                    if (krForm !== null) {
                        if (props.isLandslide) {
                            const allRasters = appendRasters(krForm, true, response.osRasters) as LandslideRasters[];
                            const toValidate = allRasters.filter((raster: any) => {
                                if (props.isMaps) {
                                    return (raster.details.rodzaj === "mapa_u" || raster.details.rodzaj === "szkic" || raster.details.rodzaj === "mapa_a")
                                } else {
                                    return (raster.details.rodzaj === props.rasterType);
                                }
                            })
                            if (props.refreshValidation) {
                                props.refreshValidation(toValidate, "rastersElements");
                            }
                            newForm = { ...krForm, landslideRasters: allRasters };
                        } else {
                            const allRasters = appendRasters(krForm, false, response.tzRasters) as EndangeredAreaRasters[];
                            const toValidate = allRasters.filter((raster: any) => {
                                if (props.isMaps) {
                                    return (raster.details.rodzaj === "mapa_u" || raster.details.rodzaj === "szkic" || raster.details.rodzaj === "mapa_a")
                                }
                                else {
                                    return false;
                                }
                            })
                            if (props.refreshValidation) {
                                props.refreshValidation(toValidate, "rastersElements");
                            }
                            newForm = { ...krForm, endangeredAreaRasters: allRasters };
                        }
                    }
                    if (newForm) {
                        setkrForm(newForm);
                        setTimeout(() => {
                            props.infoSectionChangeHandler(newForm);
                            // Hide progress bars
                            setProgress(false);
                        }, 1000);
                    }
                })
        }).catch((e) => {
            setProgress(false);
            const errMsg = getErrorMessage(e, 'Próba dodania plików zakończona niepowodzeniem.');
            notifyError({ message: errMsg });
        })
    }

    const resizeFile = (file: File) => {
        const compressionOptions = {
            maxSizeMB: props.maxFileSize / 1024 / 1024,
            maxWidthOrHeight: 2000
        }
        return imageCompression(file, compressionOptions);
    }

    // Progress bars
    const getProgressBarFieldProps = (currentPercentage: number, uploadProcessStatus: UploadProcessStatus, fileName: string): ProgressBarFieldProps => {
        return {
            progressBarInfoProps: {
                currentPercentage: currentPercentage,
                fileName: fileName,
                uploadProcessStatus: uploadProcessStatus
            },
            progressBarProps: {
                currentPercentage: currentPercentage
            }
        }
    }

    // File table
    const draggableRows = props.editMode;

    const cellAction = (properties: CellContext<any, unknown>) => {
        return (<TableActions
            row={properties.row}
            objectType={"rasters"}
            deleteClickHandler={handleDeleteClick}
            editClickHandler={handleEditClick}
            closeClickHandler={handleCloseClick}
            visibilityRows={visibilityRows}
            draggableStyle={draggableRows}
            addCommentBtnVisible={!props.editMode}
            addCommentBtnClickHandler={handleAddCommentClick}
            addCommentBtnRef={getAddCommentBtnRef}
            checkIfMarkedForCommentFilter={checkIfRowMarkedForCommentFilter}
        />)
    }

    const checkIfRowMarkedForCommentFilter = (row: any) => {
        if (props.markedFieldForCommentFiltering) {
            return checkIfMarkedForCommentFilter(props.KRFieldDictForRows!, row.globalid, props.markedFieldForCommentFiltering)
        }
    }

    const handleDeleteClick = (row: any) => {
        props.clickRowHandler(row);
        props.confirmationToggle(true);
    }

    const handleEditClick = (row: any) => {
        setRowsToFoldOut([...rowsToFoldOut, row.globalid]);
        setVisibilityRows([...visibilityRows, row.globalid]);
    }

    const handleCloseClick = (row: any) => {
        setRowsToFoldOut([...rowsToFoldOut.filter((item: any) => {
            return item !== row.globalid;
        })]);
        setVisibilityRows([...visibilityRows.filter((item: any) => {
            return item !== row.globalid;
        })]);
    }

    const handleAddCommentClick = (row: any) => {
        fireAddCommentBtnOnClickForRow(props.KRFieldDictForRows!, row.globalid, `Wiersz sekcji: ${props.fieldFormNameForRows!}`, props.setNewCommentFieldDetails!);
    }

    const getAddCommentBtnRef = (node: any, row: any) => {
        getFieldRefForRow(node, props.KRFieldDictForRows!, row.globalid, props.getMapForComment!);
    }

    const prepareFoldoutContent = (row: any) => {
        const inputProps: FormFieldProps = {
            inputPlaceholder: "Wpisz opis pliku",
            type: "text",
            pristine: true,
            keepPristine: true,
            value: row.propozycjaOpisu,
            setValue: (event: any) => onChangeInput(event),
            style: {
                width: "100%"
            },
            className: "staticField",
            configValidation: props.foldoutInputValidation
        }

        const onChangeInput = (event: any) => {
            row.propozycjaOpisu = event;
            setReload(!reload)
        }

        const getBtnProps = (title: string, onClick: React.MouseEventHandler<HTMLButtonElement>): ButtonFieldProps => {
            return {
                BtnProps: {
                    onClick: onClick,
                    className: `${classNameBtn.Btn} ${classNameBtn.LargeSecondary} body-txt-medium searchBtn`
                },
                placeHolder: title,
                placeHolderStyle: stylizationVerticalAlign,
                wrapperProps: {
                    className: "searchBtnField"
                }
            };
        }

        const saveDescription = () => {
            if (krForm !== null) {
                if (props.isLandslide) {
                    const changedRasters = _changeDescription(row, (krForm as KrLandslideFormTypes).landslideRasters);
                    const data = changedRasters?.filter((raster: any) => {
                        if (props.isMaps) {
                            return (raster.details.rodzaj === "mapa_u" || raster.details.rodzaj === "szkic" || raster.details.rodzaj === "mapa_a")
                        } else {
                            return (raster.details.rodzaj === props.rasterType);
                        }
                    })
                    if (props.refreshValidation) {
                        props.refreshValidation(data, "rastersElements");
                    }
                    setkrForm({ ...krForm, landslideRasters: changedRasters });
                } else {
                    const changedRasters = _changeDescription(row, (krForm as KrEndangeredAreaFormTypes).endangeredAreaRasters);
                    const dataEndangered = changedRasters?.filter((raster: any) => {
                        if (props.isMaps) {
                            return (raster.details.rodzaj === "mapa_u" || raster.details.rodzaj === "szkic" || raster.details.rodzaj === "mapa_a")
                        }
                        return false;
                    })
                    if (props.refreshValidation) {
                        props.refreshValidation(dataEndangered, "rastersElements");
                    }
                    setkrForm({ ...krForm, endangeredAreaRasters: changedRasters });
                }
            };
            handleCloseClick(row);
        }

        return (
            <div className="filefoldout">
                <FormField {...inputProps} />
                <ButtonField {...getBtnProps("Zapisz opis", () => { saveDescription() })}></ButtonField>
            </div>
        )
    }

    const foldoutParamToCheck = (row: any) => {
        return row.globalid;
    }

    const headers = useMemo(() => {
        return {
            pliK_NAZWA: "Nazwa pliku",
            opis: "Opis",
            imagePreview: ""
        }
    }, []);

    const handleReorderRows = (data: RastersAttributes[]) => {
        if (props.isLandslide) {
            const osRasters = structuredClone((krForm as KrLandslideFormTypes)?.landslideRasters);
            if (osRasters) {
                for (let i = 0; i < data.length; i++) {
                    const toChange = osRasters.find(or => or.rasteR_GID === data[i].globalid);
                    if (toChange?.details) {
                        (toChange.details as RastersAttributes).kolejnosc = i + 1;
                    }
                }
                setkrForm({ ...krForm, landslideRasters: osRasters });
            }
        } else {
            const tzRasters = structuredClone((krForm as KrEndangeredAreaFormTypes))?.endangeredAreaRasters;
            if (tzRasters) {
                for (let i = 0; i < data.length; i++) {
                    const toChange = tzRasters.find(tr => tr.rasteR_GID === data[i].globalid);
                    if (toChange?.details) {
                        (toChange.details as RastersAttributes).kolejnosc = i + 1;
                    }
                }
                setkrForm({ ...krForm, endangeredAreaRasters: tzRasters });
            }
        }
    }

    const columnNames = useMemo(() => {
        return Object.keys(props.headers ? props.headers : headers)
    }, [headers, props.headers])

    const columnHelper = useMemo(() => {
        return createColumnHelper<HeadersTable>();
    }, [])

    return (
        <>
            {props.editMode &&
                <>
                    <Dropzone multiple
                        disabled={progress}
                        acceptedFiles={props.acceptedFiles}
                        onConfirm={handleConfirm}
                        acceptedFilesForSnackbar={props.acceptedFilesForSnackbar}
                        dropzoneText={props.dropzoneText} />
                    <br />
                    {progress && uploadedImages.map(image => (
                        <Fragment key={image.localUUID}>
                            <ProgressBarField {...getProgressBarFieldProps(image.uploadPercent!, image.currentUploadStatus!, image.pliK_NAZWA!)} />
                            <br />
                        </Fragment>
                    ))}
                    <br /><br />
                </>
            }
            <Table
                columnAliases={props.headers ? props.headers : headers}
                columnHelper={columnHelper}
                columnNames={columnNames}
                cellActions={cellAction}
                data={tableData}
                loading={false}
                className="fileTable"
                draggableRows={draggableRows}
                foldoutRows
                rowsToFoldOut={rowsToFoldOut}
                foldoutContent={prepareFoldoutContent}
                foldoutParamToCheck={foldoutParamToCheck}
                tableError={props.tableError}
                tableErrorMsg={props.tableErrorMsg}
                setCommentsFilter={(row: any) => { if (props.setCommentsFilter) props.setCommentsFilter(getKRDictFieldNameForRow(props.KRFieldDictForRows!, row.globalid)); }}
                checkIfMarkedForCommentFilter={(row: any) => { checkIfRowMarkedForCommentFilter(row) }}
                handleReorderRows={handleReorderRows}
                narrowTableForCommentBtns={!props.editMode}
                refocus={props.refocus}
                setRefocusTable={props.setRefocusTable} />
            <br />
        </>
    )
}

export default UploadRasters;